import React from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, CheckCircle2, Clock, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import TruncatedText from "@/components/common/TruncatedText";
import { cn } from "@/lib/utils";
import { formatDateTime, formatRelativeTime } from "@/utils/dateUtils";
import { KillSwitchState } from "@/types/module-kill-switch";
import { ModuleEffectsList } from "./ModuleEffectsList";

interface ModuleStateCardProps {
  state: KillSwitchState;
  onToggle: (state: KillSwitchState) => void;
  disabled?: boolean;
}

export const ModuleStateCard: React.FC<ModuleStateCardProps> = ({
  state,
  onToggle,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const moduleLabel = t(`modules.codes.${state.module}.label`);
  const isOn = state.enabled;

  return (
    <Card
      className={cn(
        "flex flex-col gap-4 overflow-hidden",
        !isOn && "border-custom-destructive-dark/40",
      )}
    >
      <CardContent className="flex flex-col gap-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex flex-col gap-2 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-xl font-semibold text-foreground leading-tight">
                {moduleLabel}
              </h3>
              <span className="text-xs font-mono text-muted-foreground px-2 py-0.5 rounded bg-muted/40">
                {state.module}
              </span>
            </div>
            {isOn ? (
              <Badge
                variant="success"
                className="gap-1.5 text-sm font-semibold px-3 py-1"
              >
                <CheckCircle2 className="size-3.5" />
                {t("modules.states.on")}
              </Badge>
            ) : (
              <Badge
                variant="new_destructive"
                className="gap-1.5 text-sm px-3 py-1"
              >
                <AlertTriangle className="size-3.5" />
                {t("modules.states.off")}
              </Badge>
            )}
          </div>

          <div className="shrink-0">
            <Button
              type="button"
              variant={isOn ? "new_destructive" : "new_default"}
              size="lg"
              disabled={disabled}
              onClick={() => onToggle(state)}
            >
              {isOn
                ? t("modules.actions.disable")
                : t("modules.actions.enable")}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 rounded-lg bg-muted/30 p-4">
          <MetaField
            icon={<Clock className="size-3.5" />}
            label={t("modules.states.lastToggledAt")}
          >
            {state.lastToggledAt ? (
              <>
                <div className="text-sm font-medium text-foreground">
                  {formatDateTime(state.lastToggledAt)}
                </div>
                <div className="text-xs text-muted-foreground">
                  {formatRelativeTime(state.lastToggledAt)}
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground">
                {t("modules.states.neverToggled")}
              </div>
            )}
          </MetaField>

          <MetaField
            icon={<User className="size-3.5" />}
            label={t("modules.states.lastToggledBy")}
          >
            {state.lastToggledByUserId ? (
              <TruncatedText
                limit={18}
                className="text-xs font-mono text-foreground"
              >
                {state.lastToggledByUserId}
              </TruncatedText>
            ) : (
              <span className="text-sm text-muted-foreground">—</span>
            )}
          </MetaField>

          <MetaField label={t("modules.states.lastReason")}>
            {state.lastReason ? (
              <TruncatedText
                limit={80}
                className="text-sm text-foreground break-words"
              >
                {state.lastReason}
              </TruncatedText>
            ) : (
              <span className="text-sm text-muted-foreground">—</span>
            )}
          </MetaField>
        </div>

        <ModuleEffectsList module={state.module} />
      </CardContent>
    </Card>
  );
};

interface MetaFieldProps {
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

const MetaField: React.FC<MetaFieldProps> = ({ label, icon, children }) => (
  <div className="flex flex-col gap-1 min-w-0">
    <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
      {icon}
      {label}
    </div>
    <div className="min-w-0">{children}</div>
  </div>
);
